import { motion } from "motion/react";
import { Phone, Mail, CheckCircle2, Award, CheckCircle, TrendingUp } from "lucide-react";
import { ANIMATION_VARIANTS } from "@/constants/animations";
import { CONTACT_INFO, COMPANY_INFO } from "@/constants/company";
import libroReclamacionesImg from "@/assets/images/LIBRO-DE-RECLAMACIONES.webp";

export function Footer() {
  const serviceLinks = [
    "Servicio de Ingeniería en Aire Acondicionado",
    "Servicio de ductería",
    "Venta de artículos industriales",
    "Servicio de Seguridad Laboral",
    "Redes y comunicaciones",
    "Servicios Generales",
  ];

  const subServices = ["Industrial", "Comercial", "Residencial", "Automotriz"];

  const trustSignals = [
    { icon: Award, label: "Garantía de Cumplimiento" },
    { icon: CheckCircle, label: "Calidad Técnica" },
    { icon: TrendingUp, label: "Certificación" },
  ];

  return (
    <footer className="relative bg-gradient-to-br from-[#0A2540] via-[#1A3F75] to-[#2C5AA0] text-white pt-16 md:pt-24 pb-60 md:pb-24 overflow-hidden">
      {/* Patrón de fondo */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(57,181,191,0.3),transparent_70%)]" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6">
        {/* Grid principal */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
          
          {/* Columna 1: Acerca de */}
          <motion.div
            variants={ANIMATION_VARIANTS.fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <h4 className="font-bold text-[16px] mb-8 text-white border-b border-[#39B5BF]/30 pb-3 inline-block">
              {COMPANY_INFO.name}
            </h4>
            <p className="text-white/80 text-[15px] leading-relaxed mb-6">
              Especialistas en ingeniería integral con más de 15 años de
              experiencia transformando proyectos en realidades exitosas.
            </p>
            <p className="text-white/60 text-sm italic">
              "Su inversión, nuestra responsabilidad"
            </p>
          </motion.div>

          {/* Columna 2: Servicios */}
          <motion.div
            variants={ANIMATION_VARIANTS.fadeUp}
            custom={0.1}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <h4 className="font-bold text-[16px] mb-8 text-white border-b border-[#39B5BF]/30 pb-3 inline-block">
              Nuestros Servicios
            </h4>
            <ul className="space-y-4">
              <li>
                <div className="flex items-start gap-3 text-white/90 text-[15px] font-normal mb-2">
                  <CheckCircle2
                    className="w-5 h-5 text-[#39B5BF] shrink-0 mt-0.5"
                    strokeWidth={2}
                  />
                  <span>Servicio de Ingeniería en Aire Acondicionado</span>
                </div>
                <ul className="pl-8 space-y-2">
                  {subServices.map((sub) => (
                    <li
                      key={sub}
                      className="flex items-center gap-3 text-white/60 text-[14px] font-normal"
                    >
                      <div className="w-1.5 h-1.5 bg-[#A5E0DF] rounded-sm shrink-0" />
                      {sub}
                    </li>
                  ))}
                </ul>
              </li>
              {serviceLinks.slice(1).map((link) => (
                <li
                  key={link}
                  className="flex items-start gap-3 text-white/90 text-[15px] font-normal"
                >
                  <CheckCircle2
                    className="w-5 h-5 text-[#39B5BF] shrink-0 mt-0.5"
                    strokeWidth={2}
                  />
                  <span>{link}</span>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Columna 3: Contacto */}
          <motion.div
            variants={ANIMATION_VARIANTS.fadeUp}
            custom={0.2}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <h4 className="font-bold text-[16px] mb-8 text-white border-b border-[#39B5BF]/30 pb-3 inline-block">
              Contacto Directo
            </h4>
            <ul className="space-y-8">
              <li className="flex items-start gap-4">
                <Phone
                  className="w-6 h-6 text-[#39B5BF] shrink-0 mt-1"
                  strokeWidth={2}
                />
                <div className="flex flex-col gap-4">
                  <div className="flex flex-col">
                    <span className="text-white/60 text-xs font-bold uppercase tracking-widest mb-1">
                      Informes
                    </span>
                    <a
                      href={`tel:${CONTACT_INFO.phones.informes}`}
                      className="font-mono font-medium text-white text-[16px] hover:text-[#39B5BF] transition-colors"
                    >
                      {CONTACT_INFO.phones.informes}
                    </a>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-white/60 text-xs font-bold uppercase tracking-widest mb-1">
                      Ventas
                    </span>
                    <a
                      href={`tel:${CONTACT_INFO.phones.ventas}`}
                      className="font-mono font-medium text-white text-[16px] hover:text-[#39B5BF] transition-colors"
                    >
                      {CONTACT_INFO.phones.ventas}
                    </a>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-white/60 text-xs font-bold uppercase tracking-widest mb-1">
                      Pos Venta
                    </span>
                    <a
                      href={`tel:${CONTACT_INFO.phones.postVenta}`}
                      className="font-mono font-medium text-white text-[16px] hover:text-[#39B5BF] transition-colors"
                    >
                      {CONTACT_INFO.phones.postVenta}
                    </a>
                  </div>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <Mail
                  className="w-6 h-6 text-[#39B5BF] shrink-0 mt-0.5"
                  strokeWidth={2}
                />
                <a
                  href={`mailto:${CONTACT_INFO.email}`}
                  className="text-white/90 text-[15px] font-normal hover:text-[#39B5BF] transition-colors"
                >
                  {CONTACT_INFO.email}
                </a>
              </li>
            </ul>
          </motion.div>

          {/* Columna 4: Libro de Reclamaciones */}
          <motion.div
            variants={ANIMATION_VARIANTS.fadeUp}
            custom={0.3}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="flex flex-col items-start"
          >
            <h4 className="font-bold text-[16px] mb-8 text-white border-b border-[#39B5BF]/30 pb-3 inline-block">
              Atención al Cliente
            </h4>
            <motion.a
              href="#"
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{
                y: -4,
                boxShadow: "0 12px 32px rgba(57,181,191,0.15)",
              }}
              className="transition-all w-full max-w-[200px]"
              aria-label="Ir al Libro de Reclamaciones"
            >
              <img
                src={libroReclamacionesImg}
                alt="Libro de Reclamaciones"
                className="w-full h-auto object-contain rounded-lg"
                loading="lazy"
              />
            </motion.a>
          </motion.div>

        </div> 

        {/* Ribbon inferior */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{
            duration: 0.8,
            delay: 0.3,
            ease: [0.22, 1, 0.36, 1],
          }}
         className="border-t border-[#1A3F75]/50 pt-10 md:pt-16 flex flex-col items-center gap-6 md:gap-12 text-center"
        >
          {/* Trust signals */}
          <div className="flex flex-wrap items-center justify-center gap-8 md:gap-14">
            {trustSignals.map(({ icon: Icon, label }) => (
              <div key={label} className="flex items-center gap-3">
                <Icon className="w-6 h-6 text-[#39B5BF]" strokeWidth={2} />
                <span className="text-white/80 text-sm font-bold uppercase tracking-widest">
                  {label}
                </span>
              </div>
            ))}
          </div>

          {/* Quote */}
          <blockquote className="max-w-4xl mx-auto border-l-4 border-[#39B5BF] pl-6 md:pl-8 py-3 my-4 text-left">
            <p className="text-white/90 text-[16px] md:text-[18px] leading-[1.7] italic font-normal">
              "Su inversión merece expertos que cumplen. Cada proyecto ejecutado
              es nuestra carta de presentación — y su tranquilidad, nuestra
              mayor responsabilidad."
            </p>
          </blockquote>

          {/* Copyright */}
          <p className="text-white/60 text-sm font-normal mt-4 pb-24 md:pb-0">
            &copy; {COMPANY_INFO.year} {COMPANY_INFO.name}. Todos los derechos
            reservados.
          </p>
        </motion.div>
      </div>
    </footer>
  );
}